import {pick} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import {produce} from 'immer';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {Cell, TABLE_PADDING} from 'notebook/tables/TableConstants';
import {FillPlateModalReducerActionType} from 'samples/plate-maps/fill-plate-modal/machine/FillPlateModalConstants';
import {FillPlateModalReducerDispatch} from 'samples/plate-maps/fill-plate-modal/useFillPlateModalReducer';
import {
  PlateMap,
  PlateMaterialInput,
  PlateModalAnalyticsMetadata,
  ReagentAssignmentGroup,
} from 'samples/plate-maps/PlateMapConstants';
import {
  CELL_FIELDS_TO_PICK,
  SharedTransferTableColumnKey,
  ReagentMeasurementCellsBySchemas,
  FillPlateTransferTableViewMode,
  SummaryViewTransferTableColumnKey,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {getSummaryViewIndicesByColumnKey} from 'samples/plate-maps/transfer-table/FillPlateTransferTableUtil';
import SummaryViewStructuredTableAdapter, {
  SummaryViewStructuredTable,
  SummaryViewStructuredTableRowMetadata,
} from 'samples/plate-maps/transfer-table/SummaryViewStructuredTableAdapter';
import {
  getReagentDataBySchemas,
  getSummaryViewRowsContents,
} from 'samples/plate-maps/transfer-table/SummaryViewTransferTableUtil';

export interface SummaryViewStructuredTableProps {
  /** A function to dispatch an update to the transfer table state. */
  dispatch: FillPlateModalReducerDispatch;
  /** The plate map being filled. */
  plateMap: PlateMap;
  /** In a procedure context, a list of plate-used material inputs. */
  plateMaterialInputs?: Array<PlateMaterialInput>;
  /** The set of reagent schemas in the plate. */
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  /** What indices in the plate are currently selected */
  selectedWellIndices: Set<number>;
  /** Analytics metadata included with any analytics events */
  analyticsMetadata: PlateModalAnalyticsMetadata;
}

/**
 * The Summary View of the plate-map-driven transfer table. Rolls up plate map sources and contents by
 * 'schema grouping' - the set of distinct schemas within a container. A row in this table represents all
 * sources and contents of a schema within a schema grouping.
 */
export default function SummaryViewTransferTable({
  dispatch,
  plateMap,
  plateMaterialInputs,
  reagentGroupsById,
  selectedWellIndices,
  analyticsMetadata,
}: SummaryViewStructuredTableProps): JSX.Element {
  const cid = 'fill-plate-summary-view-structured-table';
  const adapter = SummaryViewStructuredTableAdapter.getAdapter();

  const showMaterialInputColumn = plateMaterialInputs != null;
  const reagentDataBySchemas = getReagentDataBySchemas(
    plateMap,
    showMaterialInputColumn,
    selectedWellIndices
  );
  const indicesByColumnKey = getSummaryViewIndicesByColumnKey(showMaterialInputColumn);
  const rowsContentsByColumnKey = getSummaryViewRowsContents({
    plateMaterialInputs,
    reagentGroupsById,
    reagentDataBySchemas,
    showMaterialInputColumn,
  });
  const tableMetadata = {
    plateMaterialInputs: plateMaterialInputs ?? [],
    reagentGroupsById,
    showMaterialInputColumn,
    testOrderInfoById: plateMap.testOrderDilutionInfoById ?? {},
  };

  function buildStructuredTableRows(): Array<StructuredTableRow<SummaryViewStructuredTableRowMetadata>> {
    const rows: Array<StructuredTableRow<SummaryViewStructuredTableRowMetadata>> = [];
    for (const [index, rowContents] of rowsContentsByColumnKey.entries()) {
      const row = produce(
        adapter.getBlankRow(
          tableMetadata,
          index,
          adapter.getAllSupportedColumns({
            entryId: '',
            metadata: tableMetadata,
            schemas: [],
            selectorsById: {},
            structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
          })
        ),
        (draft) => {
          draft.metadata.schemaGroupingId = rowContents.schemaGroupingId;
          draft.metadata.schemaId = rowContents.schemaId;
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.SOURCE_ICON]] =
            rowContents[SharedTransferTableColumnKey.SOURCE_ICON];
          draft.cells[indicesByColumnKey[SummaryViewTransferTableColumnKey.SOURCE_SCHEMA]] =
            rowContents[SummaryViewTransferTableColumnKey.SOURCE_SCHEMA];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.SOURCE_CONCENTRATION]] =
            rowContents[SharedTransferTableColumnKey.SOURCE_CONCENTRATION];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT]] =
            rowContents[SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.TRANSFER_QUANTITY]] =
            rowContents[SharedTransferTableColumnKey.TRANSFER_QUANTITY];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT]] =
            rowContents[SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.FINAL_CONCENTRATION]] =
            rowContents[SharedTransferTableColumnKey.FINAL_CONCENTRATION];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT]] =
            rowContents[SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT];
          if (rowContents.hasOwnProperty(SharedTransferTableColumnKey.MATERIAL_INPUT)) {
            draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.MATERIAL_INPUT]] =
              rowContents[SharedTransferTableColumnKey.MATERIAL_INPUT]!;
          }
        }
      );
      rows.push(row);
    }
    return rows;
  }

  function pickWellByIndex(
    row: StructuredTableRow<SummaryViewStructuredTableRowMetadata>,
    key: SharedTransferTableColumnKey
  ): Cell {
    return pick(row.cells[indicesByColumnKey[key]], ...CELL_FIELDS_TO_PICK);
  }

  function onUpdate(structuredTable: SummaryViewStructuredTable): void {
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.EDITED_TRANSFER_TABLE, {
      ...analyticsMetadata,
      viewMode: FillPlateTransferTableViewMode.SUMMARY,
    });
    const reagentMeasurementCellsBySchemas: ReagentMeasurementCellsBySchemas = {};
    for (const row of structuredTable.rows) {
      const {schemaGroupingId, schemaId} = row.metadata;
      if (!Object.keys(reagentMeasurementCellsBySchemas).includes(schemaGroupingId)) {
        reagentMeasurementCellsBySchemas[schemaGroupingId] = {
          transferQuantity: pickWellByIndex(row, SharedTransferTableColumnKey.TRANSFER_QUANTITY),
          transferQuantityUnit: pickWellByIndex(row, SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT),
          schemas: {},
        };
      }
      reagentMeasurementCellsBySchemas[schemaGroupingId].schemas[schemaId] = {
        sourceConcentration: pickWellByIndex(row, SharedTransferTableColumnKey.SOURCE_CONCENTRATION),
        sourceConcentrationUnit: pickWellByIndex(row, SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT),
        finalConcentration: pickWellByIndex(row, SharedTransferTableColumnKey.FINAL_CONCENTRATION),
        finalConcentrationUnit: pickWellByIndex(row, SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT),
      };
      if (showMaterialInputColumn) {
        reagentMeasurementCellsBySchemas[schemaGroupingId].schemas[schemaId].materialInput = pickWellByIndex(
          row,
          SharedTransferTableColumnKey.MATERIAL_INPUT
        );
      }
    }
    dispatch({
      type: FillPlateModalReducerActionType.UPDATE_REAGENT_MEASUREMENTS_BY_SCHEMAS,
      reagentMeasurementCellsBySchemas,
    });
  }

  const table = adapter.createTable({
    name: '',
    metadata: tableMetadata,
    entryId: null,
    schemas: [],
    selectorsById: {},
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  });
  table.rows = buildStructuredTableRows();

  return (
    <div className={css(styles.tableWrapper)}>
      <StructuredTableOutsideNotebook
        canRename={false}
        cid={cid}
        hideColumnHeaderIcons={true}
        includeUnsubmittedStatus={false}
        isCollapsible={false}
        structuredTable={{...table, cid}}
        suppressTitleBar={true}
        updateHandler={onUpdate}
        enableFormulas={false}
        preserveMergedCellsInPaste={true}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  tableWrapper: {
    marginLeft: -TABLE_PADDING,
  },
});
